import React, { useRef } from 'react';
import { cn } from '../../lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// RICH INPUT — contentEditable composer with atomic reference chips
//
// A contentEditable div, not a textarea: `@[File: x]` tags render as
// non-editable chips that delete as a unit. The plain-text value is
// reconstructed from the DOM on every input event.
// ═══════════════════════════════════════════════════════════════════════════════

const RichInput = React.forwardRef(({
    value, onChange, onKeyDown, onPaste, onFocus, onBlur, onChipDelete,
    placeholder, className, disabled,
}, ref) => {
    const internalRef = useRef(null);

    // Expose the DOM node with an added focusEnd() helper, so existing callers
    // that treat this ref as an element keep working.
    React.useImperativeHandle(ref, () => {
        const node = internalRef.current;
        if (node) {
            node.focusEnd = () => {
                node.focus();
                if (typeof window.getSelection !== "undefined" && typeof document.createRange !== "undefined") {
                    const range = document.createRange();
                    range.selectNodeContents(node);
                    range.collapse(false);
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            };
        }
        return node;
    });

    // Convert plain text with tags @[File: ...] or @[Task: ...] to HTML with chips
    const textToHtml = (text) => {
        if (!text) return '';
        // Escape HTML before any interpolation below. Quotes matter as much as
        // angle brackets here: the tag text is interpolated into the data-tag
        // attribute, so a file named `x" onmouseover="…` would otherwise escape
        // the attribute. Entities round-trip correctly — getAttribute() decodes
        // them, so reconstructValue() still sees the original text.
        let html = text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

        // Replace File tags with purple chips
        html = html.replace(/@\[File: ([^\]]+)\]/g, (match, content) => {
            const displayText = content.replace('File: ', '');
            return `<span contenteditable="false" class="group/chip inline-flex items-center gap-1 bg-purple-500/10 text-purple-300 pl-2 pr-1 h-5 rounded-md text-xs font-medium border border-purple-500/20 align-middle mx-1 select-none hover:bg-purple-500/20 transition-all cursor-default shadow-sm shadow-purple-900/10" data-tag="${match}">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text shrink-0 opacity-70"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <span class="max-w-[180px] truncate leading-none pb-[1px]">${displayText}</span>
                        <span class="delete-chip flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/20 cursor-pointer text-white/40 hover:text-white transition-all opacity-0 group-hover/chip:opacity-100" role="button" title="Remove reference">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        </span>
                    </span>`;
        });

        // Replace Task tags with green chips
        html = html.replace(/@\[Task: ([^\]]+)\]/g, (match, content) => {
            return `<span contenteditable="false" class="group/chip inline-flex items-center gap-1 bg-green-500/10 text-green-300 pl-2 pr-1 h-5 rounded-md text-xs font-medium border border-green-500/20 align-middle mx-1 select-none hover:bg-green-500/20 transition-all cursor-default shadow-sm shadow-green-900/10" data-tag="${match}">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 opacity-70"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        <span class="max-w-[180px] truncate leading-none pb-[1px]">${content}</span>
                        <span class="delete-chip flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/20 cursor-pointer text-white/40 hover:text-white transition-all opacity-0 group-hover/chip:opacity-100" role="button" title="Remove reference">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        </span>
                    </span>`;
        });

        return html;
    };

    // Reflect external value changes to innerHTML.
    // While focused we trust the DOM (the user is typing) to avoid cursor jumps,
    // except when the value is cleared — browsers leave a stray <br> behind, so
    // the reset has to be explicit.
    React.useEffect(() => {
        if (internalRef.current) {
            const isFocused = document.activeElement === internalRef.current;
            const currentInnerText = internalRef.current.innerText;

            if (value === '' && currentInnerText.trim() !== '') {
                internalRef.current.innerHTML = '';
            }
            else if (!isFocused && value !== currentInnerText) {
                // External update while not focused (e.g. "Add to Chat", initial load)
                internalRef.current.innerHTML = textToHtml(value);
            }
        }
    }, [value]);

    // Force update on mount, or when the value changes while not editing.
    React.useLayoutEffect(() => {
        if (internalRef.current && (document.activeElement !== internalRef.current)) {
            internalRef.current.innerHTML = textToHtml(value);
        }
    }, [value]);

    const reconstructValue = (container) => {
        let newValue = '';
        const walk = (node) => {
            if (node.nodeType === 3) { // Text
                newValue += node.textContent;
            } else if (node.nodeType === 1) { // Element
                if (node.hasAttribute('data-tag')) {
                    newValue += node.getAttribute('data-tag');
                } else if (node.tagName === 'BR') {
                    newValue += '\n';
                } else if (node.tagName === 'DIV' && newValue.length > 0) {
                    // Chrome adds divs for newlines sometimes
                    newValue += '\n';
                    node.childNodes.forEach(walk);
                } else {
                    node.childNodes.forEach(walk);
                }
            }
        };
        container.childNodes.forEach(walk);
        return newValue.replace(/\n\n/g, '\n');
    };

    const handleInput = (e) => {
        const newValue = reconstructValue(e.target);
        onChange({ target: { value: newValue } });
    };

    // Click handler to detect delete button clicks
    const handleClick = (e) => {
        const deleteBtn = e.target.closest('.delete-chip');
        if (deleteBtn) {
            e.preventDefault();
            e.stopPropagation();

            const chip = deleteBtn.closest('[data-tag]');
            if (chip) {
                const tagContent = chip.getAttribute('data-tag');
                chip.remove();

                if (internalRef.current) {
                    const newValue = reconstructValue(internalRef.current);
                    onChange({ target: { value: newValue } });
                }

                if (onChipDelete && tagContent) {
                    onChipDelete(tagContent);
                }
            }
        }
    };

    return (
        <div
            ref={internalRef}
            contentEditable={!disabled}
            onInput={handleInput}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={handleClick}
            role="textbox"
            aria-multiline="true"
            aria-label="Message"
            data-testid="chat-input"
            className={cn(
                "min-h-[56px] w-full rounded-md border border-input bg-transparent px-4 py-3 text-[15px] shadow-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 whitespace-pre-wrap break-words outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground",
                className
            )}
            style={{
                minHeight: '56px',
                maxHeight: '200px',
                overflowY: 'auto'
            }}
            data-placeholder={placeholder}
        />
    );
});
RichInput.displayName = "RichInput";

export { RichInput };
export default RichInput;
